import { all, call, put, select, takeLatest } from 'redux-saga/effects'
import BAPLIE_FILE_TYPES from 'constants/baplieFileTypes'
import { dateAndYear } from 'constants/time'
import featureCodes from 'constants/featureCodes'
import fileDownload from 'js-file-download'
import { format } from 'date-fns'
import i18next from 'i18next'
import { isFeatureEnabledSelector } from 'domain/FeatureAccess/selectors'
import reportError from 'domain/reportError'

import * as actions from './actions'
import * as appActions from '../App/actions'
import * as portCallSelectors from './selectors'
import * as userActions from '../User/actions'
import * as userSelectors from '../User/selectors'
import actionTypes from './actionTypes'
import { buildMessageContent } from './utils'
import restUtils from '../restUtils'

const endpoint = {
  baplieRestowDownload: 'file-history/download',
  baplieRestowHistory: 'file-history/history',
  baplieUpload: 'baplie/upload',
  bayLock: 'bay-lock',
  comments: 'comments',
  containerMovesProgress: 'container-moves-progress',
  containerNotes: 'container-note',
  departureEstimation: 'departure-estimation',
  draftDensity: 'draft-density',
  draftSurvey: 'draft-survey',
  gantryMovesSummary: 'gantry-moves-summary',
  gearboxLocations: 'gearbox-locations',
  general: 'general',
  logbookRemarks: 'logbook-remarks',
  milestones: 'milestones',
  portcall: 'portcall',
  productivity: 'productivity',
  reportsSummary: 'reports-summary',
  restowUpload: 'restow/upload',
  root: 'port-call',
  shipOverview: 'ship-overview',
}

function* requestPortCallGeneral(action) {
  return yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.general}`,
    setRequestSuccessful: actions.portCallGeneralRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallGeneral,
  })
}

function* requestDepartureEstimation(action) {
  return yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.departureEstimation}`,
    setRequestSuccessful: actions.portCallDepartureEstimationRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallDepartureEstimation,
  })
}

function* requestLogbookRemarks(action) {
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.logbookRemarks}`,
    setRequestSuccessful: actions.portCallLogbookRemarksRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallLogbookRemarks,
  })
}

function* requestContainerNotes(action) {
  // Check if the Container Notes is enabled
  const isEnabled = yield select(
    isFeatureEnabledSelector,
    featureCodes.SHOW_CONTAINER_NOTES
  )

  // Disabled - return empty array without making API call
  if (!isEnabled) {
    yield put(actions.setRequestingPortCallContainerNotes(true))
    yield put(actions.portCallContainerNotesRequestSuccessful([]))
    yield put(actions.setRequestingPortCallContainerNotes(false))

    return
  }

  // Enabled - make API call
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.containerNotes}`,
    setRequestSuccessful: actions.portCallContainerNotesRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallContainerNotes,
  })
}

function* requestContainerMovesProgress(action) {
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.containerMovesProgress}`,
    setRequestSuccessful:
      actions.portCallContainerMovesProgressRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallContainerMovesProgress,
  })
}

function* requestGantryMovesSummary(action) {
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.gantryMovesSummary}`,
    setRequestSuccessful: actions.portCallGantryMovesSummaryRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallGantryMovesSummary,
  })
}

function* requestSummaryTabContent(action) {
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.reportsSummary}`,
    setRequestSuccessful: actions.portCallReportsSummaryRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallReportsSummary,
  })

  const reportsSummary = yield select(
    portCallSelectors.portCallReportsSummarySelector
  )

  if (!reportsSummary) {
    yield all([
      call(requestContainerMovesProgress, action),
      call(requestGantryMovesSummary, action),
    ])
  }
}

function* requestPortCallMilestones(action) {
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.milestones}`,
    setRequestSuccessful: actions.portCallMilestonesRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallMilestones,
  })
}

function* requestPortCallSummaryTab(action) {
  yield all([
    call(requestSummaryTabContent, action),
    call(requestPortCallMilestones, action),
  ])
}

function* requestPortCallShipOverviewTab(action) {
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.shipOverview}`,
    setRequestSuccessful: actions.portCallShipOverviewRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallShipOverview,
  })
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.draftSurvey}`,
    setRequestSuccessful: actions.portCallDraftSurveyRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallDraftSurvey,
  })
  yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.gearboxLocations}`,
    setRequestSuccessful: actions.portCallGearboxLocationsRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallGearboxLocations,
  })
}

function* requestPortCallProductivity(action) {
  return yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.productivity}`,
    setRequestSuccessful: actions.portCallProductivityRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallProductivity,
  })
}

function* downloadPortCallReport(action) {
  const response = yield restUtils.downloadPortCallReport({
    imoNumber: action.payload.imoNumber,
    portCallId: action.payload.portCallId,
  })

  if (response && response.body) {
    const createdAtText = format(
      new Date(action.payload.createdAt),
      dateAndYear
    )

    const filename = `${createdAtText} - ${action.payload.shipName} - ${action.payload.portName} - port-call-report.pdf`

    fileDownload(response.body, filename)
  }
}

function* requestPortCallBaplieRestowHistory(action) {
  console.log(
    'Requesting Baplie history for port call ID:',
    action.payload.portCallId
  )

  yield restUtils.get({
    endpoint: `${endpoint.baplieRestowHistory}/${action.payload.portCallId}`,
    setRequestSuccessful: actions.portCallBaplieRestowHistoryRequestSuccessful,
    setRequestUnsuccessful: actions.portCallBaplieRestowHistoryRequestFailed,
    setRequestingState: actions.setRequestingPortCallBaplieRestowHistory,
  })
}

const mapBaplieTypeForApi = type => {
  switch (type) {
    case BAPLIE_FILE_TYPES.LOAD:
      return 'Load'
    case BAPLIE_FILE_TYPES.DISCHARGE:
      return 'Discharge'
    case BAPLIE_FILE_TYPES.RESTOW:
      return 'Restow'
    default:
      return type
  }
}

function* uploadPortCallBaplieRestow(action) {
  const { file, filename, onError, onSuccess, portCallId, type } =
    action.payload

  try {
    const isRestow = type === BAPLIE_FILE_TYPES.RESTOW

    const content = isRestow
      ? {
          file,
          filename,
          portCallId,
        }
      : {
          file,
          filename,
          portCallId,
          type: mapBaplieTypeForApi(type),
        }

    const response = yield restUtils.post({
      content,
      endpoint: isRestow ? endpoint.restowUpload : endpoint.baplieUpload,
      setRequestingState: actions.setUploadingPortCallBaplieRestow,
    })

    if (response && response.ok) {
      const statusCode = response.body && response.body.statusCode
      const statusMessage = response.body && response.body.statusMessage

      if (!statusCode || statusCode === 200) {
        // Successful upload
        yield put(actions.portCallBaplieRestowUploadSuccessful())
        yield put(actions.requestPortCallBaplieRestowHistory(portCallId))

        // Refresh ship overview after successful upload
        yield restUtils.get({
          endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.shipOverview}`,
          setRequestSuccessful: actions.portCallShipOverviewRequestSuccessful,
          setRequestingState: actions.setRequestingPortCallShipOverview,
        })

        if (onSuccess) {
          onSuccess()
        }
      } else {
        // Backend returns HTTP 200 with an application-level error code
        const errorMessage =
          statusMessage ||
          i18next.t('portCall.shipOverview.baplieOrRestowInfo.fileUploadFailed')
        yield put(actions.portCallBaplieRestowUploadFailed(errorMessage))
        if (onError) {
          onError(errorMessage)
        }
      }
    } else {
      // Any non-200 HTTP status is treated as a generic upload failure
      const errorMessage =
        (response && response.body && response.body.statusMessage) ||
        i18next.t('portCall.shipOverview.baplieOrRestowInfo.fileUploadFailed')

      yield put(actions.portCallBaplieRestowUploadFailed(errorMessage))

      if (onError) {
        onError(errorMessage)
      }
    }
  } catch (error) {
    yield put(
      actions.portCallBaplieRestowUploadFailed(error?.message || 'ERROR')
    )

    if (onError) {
      onError(error?.message || 'ERROR')
    }
  }
}

function* downloadPortCallBaplieRestow(action) {
  const { fileId, filename, onError, onSuccess, type } = action.payload

  try {
    yield put(actions.setDownloadingPortCallBaplieRestow(true))
    const response = yield restUtils.downloadBaplieRestowFile(fileId, type)

    if (response && response.body) {
      fileDownload(response.body, filename)

      if (onSuccess) {
        onSuccess()
      }

      return
    }

    yield put(actions.portCallBaplieRestowDownloadFailed('DOWNLOAD_FAILED'))

    if (onError) {
      onError()
    }
  } catch (error) {
    yield put(
      actions.portCallBaplieRestowDownloadFailed(error?.message || 'ERROR')
    )

    if (onError) {
      onError()
    }
  } finally {
    yield put(actions.setDownloadingPortCallBaplieRestow(false))
  }
}

function* downloadTerminalPerformanceReport(action) {
  const response = yield restUtils.downloadTerminalPerformanceReport(
    action.payload.portCallId
  )

  if (response && response.body) {
    const createdAtText = format(
      new Date(action.payload.createdAt),
      dateAndYear
    )

    const filename = `${createdAtText} - ${action.payload.shipName} - ${action.payload.portName} - terminal-performance-report.csv`

    fileDownload(response.body, filename)
  }
}

function* requestPortCallComments(action) {
  return yield restUtils.get({
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.comments}`,
    setRequestSuccessful: actions.portCallCommentsRequestSuccessful,
    setRequestingState: actions.setRequestingPortCallComments,
  })
}

function* requestPortCallCommentsTab(action) {
  yield all([
    call(requestPortCallComments, action),
    call(requestLogbookRemarks, action),
    call(requestContainerNotes, action),
  ])
}

function* postPortCallComment(action) {
  const SUCCESS_STATUS_CODE = 200
  const response = yield restUtils.post({
    content: action.payload.comment,
    endpoint: `${endpoint.root}/${action.payload.portCallId}/${endpoint.comments}`,
    setRequestingState: actions.setIsPostingComment,
  })

  if (!!response && response.status === SUCCESS_STATUS_CODE) {
    action.payload.resetForm()
    yield put(actions.clearCommentDraft())
    yield requestPortCallComments(action)
  }
}

function* sendPortCallFeedback(action) {
  const { portCallId, isPortCallClosed, feedback } = action.payload

  try {
    const username = yield select(userSelectors.signedInAsSelector)
    const general = yield select(portCallSelectors.portCallGeneralSelector)

    const message = buildMessageContent({ feedback, general, portCallId })

    yield put(userActions.sendFeedback({ message, name: username }))

    if (isPortCallClosed) {
      const feedbackIds = feedback.map(response => response.id)

      yield put(
        appActions.setFeedbackIdsForPortCall({ feedbackIds, portCallId })
      )
    }
  } catch (error) {
    reportError(`Failed to send port call feedback: ${error}`)
  }
}

function* savePortCallDraftSurveySaga({ payload }) {
  const { type, payload: body, onSuccess } = payload
  const SUCCESS_STATUS_CODE = 200

  yield put(actions.setRequestingSavePortCallDraftSurvey(true))
  yield put(actions.setSavePortCallDraftSurveyError(null))

  try {
    const response = yield restUtils.post({
      content: body,
      endpoint: `${endpoint.portcall}/${endpoint.draftDensity}?type=${type}`,
      setRequestingState: actions.setRequestingSavePortCallDraftSurvey,
    })

    if (response?.status === SUCCESS_STATUS_CODE) {
      yield put(actions.setRequestingSavePortCallDraftSurvey(false))

      // Refresh draft survey data after successful save
      yield restUtils.get({
        endpoint: `${endpoint.root}/${body.portCallId}/${endpoint.draftSurvey}`,
        setRequestSuccessful: actions.portCallDraftSurveyRequestSuccessful,
        setRequestingState: actions.setRequestingPortCallDraftSurvey,
      })

      if (onSuccess) {
        // UI callback
        onSuccess()
      }
    } else {
      throw new Error('SAVE_FAILED')
    }
  } catch (error) {
    yield put(actions.setRequestingSavePortCallDraftSurvey(false))
    yield put(
      actions.setSavePortCallDraftSurveyError('portCall.draftSurvey.saveFailed')
    )
  }
}

function* unlockPortCallDeviceBaySaga(action) {
  const { bayNumber, deviceId, onSettled, portCallId } = action.payload
  const SUCCESS_STATUS_CODE = 200
  let isSuccessful = false

  yield put(actions.setRequestingUnlockPortCallDeviceBay(deviceId))
  yield put(actions.setUnlockPortCallDeviceBayError(null))

  try {
    const userSelectedTime = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss.SSS")

    const response = yield restUtils.putRequest(
      `${endpoint.bayLock}/${portCallId}/${bayNumber}?lock=false&userSelectedTime=${encodeURIComponent(userSelectedTime)}`
    )

    if (response?.status === SUCCESS_STATUS_CODE) {
      isSuccessful = true

      // Refresh ship overview data so the unlocked bay/device disappears
      yield restUtils.get({
        endpoint: `${endpoint.root}/${portCallId}/${endpoint.shipOverview}`,
        setRequestSuccessful: actions.portCallShipOverviewRequestSuccessful,
        setRequestingState: actions.setRequestingPortCallShipOverview,
      })
    } else {
      throw new Error('UNLOCK_FAILED')
    }
  } catch (error) {
    reportError(`Failed to unlock device bay. ${error}`)
    yield put(
      actions.setUnlockPortCallDeviceBayError(
        'portCall.shipOverview.deviceDetailsInfo.unlockFailed'
      )
    )
  } finally {
    yield put(actions.setRequestingUnlockPortCallDeviceBay(null))

    if (onSettled) {
      onSettled(isSuccessful)
    }
  }
}

export function* watchSendPortCallFeedback() {
  yield takeLatest(actionTypes.SEND_PORT_CALL_FEEDBACK, sendPortCallFeedback)
}

export function* watchDownloadTerminalPerformanceReport() {
  yield takeLatest(
    actionTypes.DOWNLOAD_TERMINAL_PERFORMANCE_REPORT,
    downloadTerminalPerformanceReport
  )
}

export function* watchDownloadPortCallReport() {
  yield takeLatest(
    actionTypes.DOWNLOAD_PORT_CALL_REPORT,
    downloadPortCallReport
  )
}

export function* watchDownloadPortCallBaplieRestow() {
  yield takeLatest(
    actionTypes.DOWNLOAD_PORT_CALL_BAPLIE_RESTOW,
    downloadPortCallBaplieRestow
  )
}

export function* watchRequestPortCallGeneral() {
  yield takeLatest(
    actionTypes.REQUEST_PORT_CALL_GENERAL,
    requestPortCallGeneral
  )
}

export function* watchRequestPortCallProductivity() {
  yield takeLatest(
    actionTypes.REQUEST_PORT_CALL_PRODUCTIVITY,
    requestPortCallProductivity
  )
}

export function* watchRequestPortCallSummaryTab() {
  yield takeLatest(
    actionTypes.REQUEST_PORT_CALL_SUMMARY_TAB,
    requestPortCallSummaryTab
  )
}

export function* watchRequestPortCallShipOverviewTab() {
  yield takeLatest(
    actionTypes.REQUEST_PORT_CALL_SHIP_OVERVIEW_TAB,
    requestPortCallShipOverviewTab
  )
}

export function* watchRequestPortCallDepartureEstimation() {
  yield takeLatest(
    actionTypes.REQUEST_PORT_CALL_DEPARTURE_ESTIMATION,
    requestDepartureEstimation
  )
}

export function* watchRequestPortCallCommentsTab() {
  yield takeLatest(
    actionTypes.REQUEST_PORT_CALL_COMMENTS_TAB,
    requestPortCallCommentsTab
  )
}

export function* watchPostPortCallComment() {
  yield takeLatest(actionTypes.POST_PORT_CALL_COMMENT, postPortCallComment)
}

export function* watchSavePortCallDraftSurvey() {
  yield takeLatest(
    actionTypes.SAVE_PORT_CALL_DRAFT_SURVEY,
    savePortCallDraftSurveySaga
  )
}

export function* watchUnlockPortCallDeviceBay() {
  yield takeLatest(
    actionTypes.UNLOCK_PORT_CALL_DEVICE_BAY,
    unlockPortCallDeviceBaySaga
  )
}

export function* watchRequestPortCallBaplieRestowHistory() {
  yield takeLatest(
    actionTypes.REQUEST_PORT_CALL_BAPLIE_RESTOW_HISTORY,
    requestPortCallBaplieRestowHistory
  )
}

export function* watchUploadPortCallBaplieRestow() {
  yield takeLatest(
    actionTypes.UPLOAD_PORT_CALL_BAPLIE_RESTOW,
    uploadPortCallBaplieRestow
  )
}
